import { useEffect, useRef, useState } from 'react';
import type { OrgBrandingDto } from '@shared/api-dto';
import { useSession } from '../auth/AuthProvider';
import { ApiError } from '../api/errors';
import { LOGO_ACCEPT, uploadOrgLogo } from '../branding/upload';

/**
 * Firm-logo section of the organization page. The logo is an ORGANIZATION asset — it white-labels the
 * whole app shell and the public Claro onboarding Room — so it lives here, in org admin, not in any
 * product. Editing is gated on `org:branding:edit` (owner/admin); reading is open to anyone who can
 * see this page.
 */
export function OrgBrandingSection() {
  const { client, can } = useSession();
  const mayEdit = can('org:branding:edit');

  const [state, setState] = useState<OrgBrandingDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    client
      .getOrgBranding()
      .then((s) => live && setState(s))
      .catch(() => live && setState({ logo: null, logoUrl: null }));
    return () => {
      live = false;
    };
  }, [client]);

  const errText = (e: unknown, fallback: string) =>
    e instanceof ApiError ? e.message : e instanceof Error ? e.message : fallback;

  async function onPick(file: File) {
    setError(null);
    setBusy(true);
    try {
      setState(await uploadOrgLogo(client, file));
    } catch (e) {
      setError(errText(e, 'העלאת הלוגו נכשלה. נסו שוב.'));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = ''; // allow re-picking the same file after a failure
    }
  }

  async function onRemove() {
    setError(null);
    setBusy(true);
    try {
      await client.deleteOrgLogo();
      setState({ logo: null, logoUrl: null });
    } catch (e) {
      setError(errText(e, 'ההסרה נכשלה. נסו שוב.'));
    } finally {
      setBusy(false);
    }
  }

  const logoUrl = state?.logoUrl ?? null;

  return (
    <section className="huaca-branding" data-testid="org-branding">
      <h2>לוגו המשרד</h2>
      <p className="huaca-branding-hint">מחליף את סמל המערכת בראש האפליקציה ובכל קישור קליטה שתשלחו ללקוחות.</p>

      <div className="huaca-logo-row">
        {logoUrl ? (
          <img className="huaca-logo-preview" src={logoUrl} alt="לוגו המשרד" data-testid="org-logo-preview" />
        ) : (
          <div className="huaca-logo-placeholder" aria-hidden="true">
            ללא לוגו
          </div>
        )}
        {mayEdit && (
          <div className="huaca-logo-actions">
            <input
              ref={inputRef}
              type="file"
              accept={LOGO_ACCEPT}
              hidden
              data-testid="org-logo-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onPick(f);
              }}
            />
            <button type="button" className="btn-ghost" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="org-logo-upload">
              {busy ? 'מעלה…' : logoUrl ? 'החלפת לוגו' : 'העלאת לוגו'}
            </button>
            {logoUrl && (
              <button type="button" className="btn-ghost huaca-logo-remove" disabled={busy} onClick={() => void onRemove()} data-testid="org-logo-remove">
                הסרה
              </button>
            )}
          </div>
        )}
      </div>

      {mayEdit && <p className="huaca-branding-hint">PNG, JPEG או WebP. תמונות גדולות יכווצו אוטומטית.</p>}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
